/* ==========================================================================
   WATCHFACTS BY NEXO — Main JS v2.0
   ========================================================================== */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // 1. SCROLL REVEAL — Intersection Observer
  // -------------------------------------------------------------------------
  function initScrollReveal() {
    const els = document.querySelectorAll('.wf-reveal, .wf-gold-line');
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('wf-revealed');
          observer.unobserve(e.target);
        }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );

    els.forEach((el) => observer.observe(el));
  }

  // -------------------------------------------------------------------------
  // 2. STICKY HEADER — Transparent → solid on scroll
  // -------------------------------------------------------------------------
  function initStickyHeader() {
    const header = document.querySelector('.wf-header');
    if (!header) return;

    const hasHero = !!document.querySelector('.wf-hero, .wf-collection-hero');
    if (!hasHero) {
      header.classList.add('wf-header--solid');
      header.classList.remove('wf-header--transparent');
      return;
    }

    let ticking = false;

    function update() {
      if (window.scrollY > 60) {
        header.classList.add('wf-header--solid');
        header.classList.remove('wf-header--transparent');
      } else {
        header.classList.remove('wf-header--solid');
        header.classList.add('wf-header--transparent');
      }
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });

    update();
  }

  // -------------------------------------------------------------------------
  // 3. CURSOR — Golden dot with comet trail (canvas-based)
  // -------------------------------------------------------------------------
  function initCursor() {
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const canvas = document.getElementById('wf-cursor-canvas');
    const dot    = document.getElementById('wf-cursor-dot');
    if (!canvas || !dot) return;

    const ctx  = canvas.getContext('2d');
    const TRAIL = 18;
    const trail = [];

    let mx = -200, my = -200;
    let dotX = -200, dotY = -200;
    let isHover = false;

    function resize() {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    document.addEventListener('mousemove', (e) => {
      mx = e.clientX;
      my = e.clientY;
    });

    document.addEventListener('mouseover', (e) => {
      if (e.target.closest('a, button, [role="button"], input, select, textarea, label')) {
        isHover = true;
        dot.classList.add('is-hover');
      }
    });
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest('a, button, [role="button"], input, select, textarea, label')) {
        isHover = false;
        dot.classList.remove('is-hover');
      }
    });

    function animate() {
      // Lerp dot position
      dotX += (mx - dotX) * 0.25;
      dotY += (my - dotY) * 0.25;

      dot.style.left = dotX + 'px';
      dot.style.top  = dotY + 'px';

      // Trail stores raw cursor pos
      trail.unshift({ x: mx, y: my });
      if (trail.length > TRAIL) trail.pop();

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      trail.forEach((pos, i) => {
        const t    = 1 - i / TRAIL;
        const size = 3 * t * t;
        const alpha = 0.5 * t;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${alpha})`;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();
  }

  // -------------------------------------------------------------------------
  // 4. MOBILE MENU
  // -------------------------------------------------------------------------
  function initMobileMenu() {
    const hamburger = document.querySelector('.wf-header__hamburger');
    const menu      = document.querySelector('.wf-mobile-menu');
    const overlay   = document.getElementById('wf-overlay');
    if (!hamburger || !menu) return;

    function open() {
      menu.classList.add('is-open');
      hamburger.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      if (overlay) overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      menu.classList.remove('is-open');
      hamburger.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      if (overlay) overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    hamburger.addEventListener('click', () =>
      menu.classList.contains('is-open') ? close() : open()
    );

    menu.querySelectorAll('.wf-mobile-menu__close').forEach(btn => btn.addEventListener('click', close));
    if (overlay) overlay.addEventListener('click', close);

    menu.querySelectorAll('.wf-mobile-menu__link').forEach(link =>
      link.addEventListener('click', close)
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  // -------------------------------------------------------------------------
  // 5. CART DRAWER
  // -------------------------------------------------------------------------
  function initCartDrawer() {
    const cartBtn  = document.querySelector('.wf-header__cart');
    const drawer   = document.querySelector('.wf-cart-drawer');
    const closeBtn = document.querySelector('.wf-cart-drawer__close');
    const overlay  = document.getElementById('wf-overlay');
    if (!cartBtn || !drawer) return;

    function open() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    cartBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    window.wfCartDrawer = { open, close };
  }

  // -------------------------------------------------------------------------
  // 6. ACCORDION
  // -------------------------------------------------------------------------
  function initAccordion() {
    document.querySelectorAll('.wf-accordion__trigger').forEach(btn => {
      btn.addEventListener('click', () => {
        const item    = btn.closest('.wf-accordion__item');
        const content = item.querySelector('.wf-accordion__body');
        const isOpen  = item.classList.contains('is-open');

        // Close all in same group
        btn.closest('.wf-accordion').querySelectorAll('.wf-accordion__item.is-open').forEach(open => {
          open.classList.remove('is-open');
          open.querySelector('.wf-accordion__body').style.maxHeight = null;
        });

        if (!isOpen) {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  // -------------------------------------------------------------------------
  // 7. ANIMATED COUNTERS
  // -------------------------------------------------------------------------
  function initCounters() {
    const counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const el     = entry.target;
        const target = parseFloat(el.dataset.counter);
        const suffix = el.dataset.suffix || '';
        const prefix = el.dataset.prefix || '';

        if (isNaN(target)) {
          el.textContent = prefix + el.dataset.counter + suffix;
          return;
        }

        const dur    = 1800;
        const start  = performance.now();

        function tick(now) {
          const elapsed  = now - start;
          const progress = Math.min(elapsed / dur, 1);
          const eased    = 1 - Math.pow(1 - progress, 3);
          const value    = Math.round(target * eased);
          el.textContent = prefix + value.toLocaleString('es-ES') + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        }

        requestAnimationFrame(tick);
      });
    }, { threshold: 0.5 });

    counters.forEach(el => observer.observe(el));
  }

  // -------------------------------------------------------------------------
  // 8. PRODUCT GALLERY
  // -------------------------------------------------------------------------
  function initProductGallery() {
    const gallery = document.querySelector('.wf-product-gallery');
    if (!gallery) return;

    const mainImg = gallery.querySelector('.wf-product-gallery__main img');
    const thumbs  = gallery.querySelectorAll('.wf-product-gallery__thumb');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src    = thumb.dataset.src || thumb.querySelector('img')?.src;
        const srcset = thumb.dataset.srcset;
        if (mainImg && src) {
          mainImg.style.opacity = '0';
          mainImg.style.transform = 'scale(0.97)';
          setTimeout(() => {
            mainImg.src = src;
            if (srcset) mainImg.srcset = srcset;
            mainImg.style.opacity = '1';
            mainImg.style.transform = 'scale(1)';
          }, 200);
        }
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    if (mainImg) {
      mainImg.style.transition = 'opacity 0.2s ease, transform 0.3s ease';
    }
  }

  // -------------------------------------------------------------------------
  // 9. VARIANT PICKER
  // -------------------------------------------------------------------------
  function initVariantPicker() {
    const pickers = document.querySelectorAll('.wf-variant-picker');
    if (!pickers.length) return;

    pickers.forEach(picker => {
      const form    = picker.closest('form');
      const hiddenInput = form?.querySelector('[name="id"]');
      const variants    = JSON.parse(picker.dataset.variants || '[]');
      const buttons     = picker.querySelectorAll('.wf-variant-btn');

      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          const optName = btn.dataset.optionName;
          const optVal  = btn.dataset.optionValue;

          picker.querySelectorAll(`[data-option-name="${optName}"]`).forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-checked', 'false');
          });

          btn.classList.add('is-active');
          btn.setAttribute('aria-checked', 'true');

          // Find matching variant
          const selected = {};
          picker.querySelectorAll('.wf-variant-btn.is-active').forEach(b => {
            selected[b.dataset.optionName] = b.dataset.optionValue;
          });

          const match = variants.find(v =>
            v.options.every((opt, i) => selected[v.option_names?.[i]] === opt || true)
          );

          if (match && hiddenInput) hiddenInput.value = match.id;
        });
      });
    });
  }

  // -------------------------------------------------------------------------
  // 10. TESTIMONIALS CAROUSEL (Swiper)
  // -------------------------------------------------------------------------
  function initTestimonials() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('.wf-testimonials-swiper').forEach(el => {
      new Swiper(el, {
        slidesPerView: 1.1,
        spaceBetween: 24,
        grabCursor: true,
        loop: true,
        autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        breakpoints: {
          640:  { slidesPerView: 1.5 },
          1024: { slidesPerView: 2.2 },
          1280: { slidesPerView: 3 }
        },
        navigation: {
          nextEl: '.wf-testimonials-next',
          prevEl: '.wf-testimonials-prev'
        }
      });
    });
  }

  // -------------------------------------------------------------------------
  // 11. PAGE TRANSITIONS
  // -------------------------------------------------------------------------
  function initPageTransitions() {
    const overlay = document.getElementById('wf-page-transition');
    if (!overlay) return;

    // Fade in on load
    overlay.classList.remove('active');

    document.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') ||
          href.startsWith('javascript:') || link.target === '_blank' || link.hasAttribute('download')) return;

      link.addEventListener('click', (e) => {
        const url = new URL(link.href, location.origin);
        if (url.origin !== location.origin) return;
        e.preventDefault();
        overlay.classList.add('active');
        setTimeout(() => { location.href = link.href; }, 400);
      });
    });
  }

  // bfcache fix: when browser restores page via back/forward, DOMContentLoaded
  // does not fire again, so the transition overlay stays black. Clear it here.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      const overlay = document.getElementById('wf-page-transition');
      if (overlay) overlay.classList.remove('active');
      // also release any scroll lock left by mobile menu or cart drawer
      document.body.style.overflow = '';
    }
  });

  // -------------------------------------------------------------------------
  // 12. TRUST BAR — infinite scroll marquee
  // -------------------------------------------------------------------------
  function initTrustBar() {
    const track = document.querySelector('.wf-trust-bar__track');
    if (!track) return;

    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.parentElement.appendChild(clone);
  }

  // -------------------------------------------------------------------------
  // 13. PRODUCT ZOOM — loupe effect on card media (desktop only)
  // -------------------------------------------------------------------------
  function initProductZoom() {
    if (window.matchMedia('(pointer: coarse)').matches) return;

    var wrap = document.querySelector('.wf-product-gallery__main');
    if (!wrap) return;

    var img = wrap.querySelector('img');
    if (!img) return;

    wrap.addEventListener('mousemove', function (e) {
      var rect = wrap.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100)).toFixed(1) + '%';
      var y = Math.max(0, Math.min(100, (e.clientY - rect.top) / rect.height * 100)).toFixed(1) + '%';
      img.style.transformOrigin = x + ' ' + y;
    });

    wrap.addEventListener('mouseleave', function () {
      img.style.transformOrigin = '';
    });
  }

  // -------------------------------------------------------------------------
  // 14. PARALLAX
  // -------------------------------------------------------------------------
  function initParallax() {
    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    function update() {
      const scrollY = window.scrollY;
      els.forEach(el => {
        const speed  = parseFloat(el.dataset.parallax) || 0.3;
        const rect   = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const offset = (center - window.innerHeight / 2) * speed;
        el.style.transform = `translateY(${offset}px)`;
      });
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // -------------------------------------------------------------------------
  // INIT
  // -------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initStickyHeader();
    initMobileMenu();
    initCartDrawer();
    initAccordion();
    initCounters();
    initProductGallery();
    initVariantPicker();
    initTestimonials();
    initTrustBar();
    initProductZoom();
    initParallax();

    // Cursor only when not reduced motion
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      initCursor();
      initPageTransitions();
    }
  });

})();
